import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { useHttp } from '../../../hooks/useHttp'
import {
    addAllVisitsReducer,
    addTranscriptReducer,
    addVisitReducer,
    deleteVisitReducer,
    deleteVisitsReducer,
    searchVisitReducer,
    updateDurationReducer,
    updateVisitReducer
} from '../../../redux/reducers/visits-slice'
import { RootState } from '../../../redux/store'
import { eventBus, EventBusData } from '../../../services/event-bus/eventBus.service'
import { VisitFailedToFinish, VisitNoteProcessed, VisitNoteProcessing } from '../../../services/event-bus/events'
import { ErrorCode } from '../../../services/http/auth.axios'
import { visitsService } from '../../../services/http/visits.service'
import { Transcript, TranscriptPartial, Visit, VisitKey, VisitsGroup, VisitType } from '../../../services/models/Visit.model'
import { VisitNote } from '../../../services/models/VisitNote.model'
import { useBillingContext } from '../../Billing/hooks/useBillingContext'

export interface VisitsHookState {
    visits: Visit[]
    visitsGroups: VisitsGroup[]
    areVisitsLoading: boolean
    areVisitsLoaded: boolean
    isCreateVisitLoading: boolean
    isDeleteVisitLoading: boolean
    getVisit(id: string): Visit | undefined
    getVisits(): Promise<void>
    createVisit(templateId: string | undefined, customTemplateId: string | undefined, type: VisitType): Promise<Visit | null>
    deleteVisit(id: string): Promise<void>
    deleteVisits(ids: string[]): Promise<void>
    updateVisit(visitPartialUpdate: VisitPartialUpdate): void
    updateDuration(id: string, duration: number, propagate?: boolean): void
    createTranscript(id: string, transcript: Transcript | TranscriptPartial): void
    searchVisits(searchTerm: string): void
}

type VisitPartialUpdate = {
    _id: string
    key: VisitKey
    value: any
    propagate?: boolean // Reduce re-renders by having less update propagation
}

export const useVisits = (userId?: string): VisitsHookState => {
    const dispatch = useDispatch()
    const state = useSelector((state: RootState) => state.visits)

    const { isSubscriptionPending, isSubscriptionPastDue } = useBillingContext().subscription

    const { visitsFiltered: visits, visitsGroups, paginationIndex } = state
    const { areVisitsLoading, areVisitsLoaded, isCreateVisitLoading, isDeleteVisitLoading, getVisitsReq, createVisitReq, deleteVisitReq, deleteVisitsReq } = useHttpReq()
    const hasFetchedRef = useRef<boolean>(false)

    const getVisit = useCallback((id: string) => visits.find((i) => i._id === id), [visits])

    const getVisits = useCallback(async () => {
        if (!userId) {
            return Promise.resolve()
        }
        return getVisitsReq(userId, paginationIndex)
            .then((_visits) => {
                dispatch(addAllVisitsReducer(_visits))
            })
            .catch((error) => console.error(error))
    }, [userId, paginationIndex, getVisitsReq, dispatch])

    const createVisit = useCallback(
        async (templateId: string | undefined, customTemplateId: string | undefined, type: VisitType) => {
            if (!userId) {
                return Promise.reject('Missing userId')
            }

            if (!type) type = 'visit'
            if (isSubscriptionPending || isSubscriptionPastDue) {
                toast.error('No active subscription')
                return Promise.reject('No active subscription')
            }
            return createVisitReq(userId, templateId, customTemplateId, type)
                .then((newVisit) => {
                    dispatch(addVisitReducer(newVisit))
                    return newVisit
                })
                .catch((error) => {
                    if ((error?.errorCode as ErrorCode) === 'LIMIT_REACHED') {
                        toast.error('You have reached the maximum number of visits allowed for your current billing period.')
                    }
                    return null
                })
        },
        [userId, isSubscriptionPending, isSubscriptionPastDue, createVisitReq, dispatch]
    )

    const updateVisit = useCallback(
        (visitPartialUpdate: VisitPartialUpdate) => {
            dispatch(updateVisitReducer(visitPartialUpdate))
        },
        [dispatch]
    )

    const updateDuration = useCallback(
        async (id: string, duration: number, propagate?: boolean) => {
            dispatch(updateDurationReducer({ _id: id, duration, propagate }))
        },
        [dispatch]
    )

    const createTranscript = useCallback(
        (id: string, transcript: Transcript | TranscriptPartial) => {
            dispatch(addTranscriptReducer({ _id: id, transcript }))
        },
        [dispatch]
    )

    const deleteVisit = useCallback(
        async (id: string) => {
            return deleteVisitReq(id)
                .then(() => {
                    dispatch(deleteVisitReducer(id))
                    return
                })
                .catch((error) => console.error(error))
        },
        [deleteVisitReq, dispatch]
    )

    const deleteVisits = useCallback(
        async (ids: string[]) => {
            return deleteVisitsReq(ids)
                .then(() => {
                    dispatch(deleteVisitsReducer(ids))
                    return
                })
                .catch((error) => console.error(error))
        },
        [deleteVisitsReq, dispatch]
    )

    const searchVisits = useCallback(
        (searchTerm: string) => {
            dispatch(searchVisitReducer(searchTerm))
        },
        [dispatch]
    )

    useEffect(() => {
        if (hasFetchedRef.current) {
            return
        }
        getVisits()
        hasFetchedRef.current = true
    }, [getVisitsReq, getVisits])

    useEffect(() => {
        const subscription = eventBus.getObservable().subscribe((event: EventBusData) => {
            switch (event.action) {
                case VisitNoteProcessing.action: {
                    const visitId = event.payload as string
                    updateVisit({
                        _id: visitId,
                        key: 'state',
                        value: 'processing',
                        propagate: true
                    })
                    break
                }

                case VisitNoteProcessed.action: {
                    const visitNote = event.payload as VisitNote

                    if (visitNote.isSystemGenerated && visitNote.state === 'completed') {
                        // TODO: Refactor these updates into simple dispatch event
                        updateVisit({
                            _id: visitNote.visitId,
                            key: 'state',
                            value: 'completed',
                            propagate: true
                        })
                        updateVisit({
                            _id: visitNote.visitId,
                            key: 'title',
                            value: visitNote.name,
                            propagate: true
                        })
                        updateVisit({
                            _id: visitNote.visitId,
                            key: 'templateId',
                            value: visitNote.templateId,
                            propagate: true
                        })
                        updateVisit({
                            _id: visitNote.visitId,
                            key: 'customTemplateId',
                            value: visitNote.customTemplateId,
                            propagate: true
                        })
                    }
                    break
                }

                // Reset visit state back to transcribing
                case VisitFailedToFinish.action: {
                    const visitId = event.payload as string
                    updateVisit({
                        _id: visitId,
                        key: 'state',
                        value: 'transcribing',
                        propagate: true
                    })
                    break
                }
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [updateVisit])

    return {
        visits,
        visitsGroups,
        areVisitsLoading,
        areVisitsLoaded,
        isCreateVisitLoading,
        isDeleteVisitLoading,
        getVisit,
        getVisits,
        createVisit,
        deleteVisit,
        deleteVisits,
        updateVisit,
        updateDuration,
        createTranscript,
        searchVisits
    }
}

enum REQUEST {
    GET_ALL,
    CREATE,
    DELETE,
    DELETE_BULK
}

const useHttpReq = () => {
    const { sendRequest, requestId, isLoading, hasLoaded } = useHttp()

    const getVisitsReq = useCallback(
        async (userId: string, index: number): Promise<Visit[]> => sendRequest(visitsService.getVisits.bind({}, userId, index), REQUEST.GET_ALL),
        [sendRequest]
    )

    const createVisitReq = useCallback(
        async (userId: string, templateId: string | undefined, customTemplateId: string | undefined, type: VisitType): Promise<Visit> =>
            sendRequest(visitsService.createVisit.bind({}, userId, templateId, customTemplateId, type), REQUEST.CREATE),
        [sendRequest]
    )

    const deleteVisitReq = useCallback(async (id: string): Promise<Visit> => sendRequest(visitsService.deleteVisit.bind({}, id), REQUEST.DELETE), [sendRequest])

    const deleteVisitsReq = useCallback(async (ids: string[]): Promise<Visit> => sendRequest(visitsService.deleteVisits.bind({}, ids), REQUEST.DELETE_BULK), [sendRequest])

    const areVisitsLoading = useMemo(() => isLoading && requestId === REQUEST.GET_ALL, [isLoading, requestId])

    const areVisitsLoaded = useMemo(() => hasLoaded && requestId === REQUEST.GET_ALL, [hasLoaded, requestId])

    const isCreateVisitLoading = useMemo(() => isLoading && requestId === REQUEST.CREATE, [isLoading, requestId])

    const isDeleteVisitLoading = useMemo(() => isLoading && (requestId === REQUEST.DELETE || requestId === REQUEST.DELETE_BULK), [isLoading, requestId])

    return {
        areVisitsLoading,
        areVisitsLoaded,
        isCreateVisitLoading,
        isDeleteVisitLoading,
        getVisitsReq,
        createVisitReq,
        deleteVisitReq,
        deleteVisitsReq
    }
}
